import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { SECRET_LEVELS, SECRET_LEVEL_LABELS } from '@infras/shared-types';
import type { SecretLevel } from '@infras/shared-types';
/** Level badge (L0 red / L1 amber / L3 muted) + the root/maintainer inline editor. */

const LEVEL_STYLE: Record<string, React.CSSProperties> = {
  critical: { background: 'rgba(220, 38, 38, 0.15)', color: '#ef4444', borderColor: '#ef4444' },
  restricted: { background: 'rgba(217, 119, 6, 0.15)', color: '#f59e0b', borderColor: '#f59e0b' },
  normal: {},
};

export function LevelBadge({ level }: { level: string }) {
  const label = SECRET_LEVEL_LABELS[level as SecretLevel] ?? level;
  return (
    <span
      className="badge"
      style={LEVEL_STYLE[level]}
      title={`sensitivity: ${level} (${label})`}
    >
      {label}
    </span>
  );
}

export const SET_SECRET_LEVEL = gql`
  mutation SetSecretLevel($name: String!, $level: String!) {
    setSecretLevel(name: $name, level: $level)
  }
`;

/**
 * Inline classifier for root/maintainer (render only when canEditSecretLevels).
 * Targets the LOGICAL secret (canonical name) — all visible env-instances move
 * together. A maintainer escalating to critical loses sight of it (one-way, by
 * design); the server scopes the UPDATE to their visible levels.
 */
export function LevelSelect({
  name,
  level,
  onChanged,
}: {
  name: string;
  level: string;
  onChanged: () => void;
}) {
  const [setLevel, { loading }] = useMutation(SET_SECRET_LEVEL, {
    onCompleted: onChanged,
  });
  return (
    <select
      className="class-select class-select--sm"
      value={level}
      disabled={loading}
      title="classify sensitivity (root/maintainer)"
      onChange={(e) => {
        const next = e.target.value;
        if (
          next === 'critical' &&
          !window.confirm(
            `Classify "${name}" as critical (L0)? Only root can see/undo critical secrets.`,
          )
        ) {
          e.target.value = level;
          return;
        }
        void setLevel({ variables: { name, level: next } });
      }}
    >
      {SECRET_LEVELS.map((l) => (
        <option key={l} value={l}>
          {SECRET_LEVEL_LABELS[l]} · {l}
        </option>
      ))}
    </select>
  );
}
